'use client';

import type {
  SeedingDetailProduct,
  SeedingFeaturedComment,
} from '@/app/(activity)/campaigns/[slug]/types';
import { getSeedingProductImageUrl } from '@/app/(activity)/campaigns/[slug]/pages/SeedingPage/utils/getSeedingMediaUrl';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import SwiperNavigationButtons from '@/components/SwiperNavigation';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useReporting } from '@/hooks/useReporting';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { HOME_PREVIEW_PATH } from '@/utils/constants';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { RightArrowOutlined } from '@zeelool/icon';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import type {
  MaterialGallery2026Props,
  MaterialGallery2026TrackingParams,
  MaterialGallery2026ValidExposureParams,
  MaterialGalleryItem,
} from './types';
import { useMaterialGalleryDetail, useMaterialGallerySummary } from './useMaterialGalleryData';
import {
  buildGalleryPageTrackingParams,
  buildMaterialTrackingParams,
  buildSeedingSummaryMap,
  getValidMaterialItems,
  normalizeConfiguredText,
} from './utils';

const COMPONENT_ID = 'materialGallery2026';
const DEFAULT_HEADING_COLOR = '#1A1C1E';
const DEFAULT_SUBTITLE_COLOR = '#626668';
const VALID_EXPOSURE_MODULE_NAME = '素材图集';
const MATERIAL_DETAIL_POPUP_TYPE = '素材对应互动详情弹窗';
const REVIEW_IMAGE_ALT_MAX_LENGTH = 70;

const loadSeedingItemDialog = () =>
  import('@/app/(activity)/campaigns/[slug]/pages/SeedingPage/components/SeedingItemDialog');
const SeedingItemDialog = dynamic(loadSeedingItemDialog, { ssr: false });

interface SelectedMaterial {
  itemId: number;
  originalIndex: number;
}

function preloadSeedingItemDialog() {
  if (typeof window !== 'undefined') {
    void loadSeedingItemDialog();
  }
}

// 首页装修埋点没有收藏事件类型；收藏业务仍由 Wish 自身处理，避免误报商品跳转。
function ignoreMaterialGalleryCollectTracking() {
  return undefined;
}

function buildValidExposureParams(
  trackingParams: MaterialGallery2026TrackingParams,
): MaterialGallery2026ValidExposureParams {
  return {
    module_index: trackingParams.module_index,
    module_id: trackingParams.module_id,
    module_title: trackingParams.module_title || '',
    module_name: VALID_EXPOSURE_MODULE_NAME,
    module_tab_name: '',
    mkt_index: trackingParams.mkt_index,
    mkt_index_sub: '',
    mkt_jump_type: trackingParams.mkt_jump_type,
    mkt_jump_content: trackingParams.mkt_jump_content,
  };
}

function useMaterialTracking({
  trackingParams,
  validExposureParams,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: Pick<MaterialGallery2026Props, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'> & {
  trackingParams: MaterialGallery2026TrackingParams;
  validExposureParams: MaterialGallery2026ValidExposureParams;
}) {
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('2026 PC material gallery exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      onTrack(validExposureParams);
    } catch (error) {
      logger.error('2026 PC material gallery click tracking error:', error);
    }
  }, [onTrack, validExposureParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(validExposureParams),
  });

  return {
    cardRef: useMergedRefs(exposureRef, validExposureRef),
    handleClick,
  };
}

function MaterialProductPreview({ product }: { product?: SeedingDetailProduct }) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const productDisplayName = product?.short_name || product?.name || '';
  const productImage = getSeedingProductImageUrl(product?.image);

  const sellPrice = useMemo(() => {
    if (!product) return '';
    const resolvedPrice = priceInfo(userInfo, product, currency);
    const price = resolvedPrice.sell_price || product.price || '';
    return price ? formatPriceNumberWithSymbol(String(price), currency) : '';
  }, [currency, product, userInfo]);

  if (!product) {
    return (
      <div className="w-full animate-pulse rounded-[12px] bg-primary-white p-[12px]" aria-hidden>
        <div className="flex items-center gap-[12px]">
          <div className="h-[72px] w-[104px] rounded-[8px] bg-auxiliary-light-gray" />
          <div className="flex flex-1 flex-col gap-[10px]">
            <div className="h-[16px] w-4/5 rounded bg-auxiliary-light-gray" />
            <div className="h-[18px] w-2/5 rounded bg-auxiliary-light-gray" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-[12px] bg-primary-white p-[12px] shadow-[0_4px_16px_rgba(0,0,0,0.16)]">
      <div className="flex items-center gap-[12px]">
        {productImage ? (
          <div className="h-[72px] w-[104px] shrink-0 overflow-hidden rounded-[8px] bg-auxiliary-light-gray">
            <Image
              src={productImage}
              alt={productDisplayName}
              width={208}
              height={144}
              className="h-full w-full object-contain mix-blend-multiply"
            />
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          {productDisplayName ? (
            <p className="line-clamp-2 font-br text-[14px] leading-[17px] text-primary-text">
              {productDisplayName}
            </p>
          ) : null}
          {sellPrice ? (
            <p className="mt-[8px] font-bb text-[16px] leading-[19px] text-primary-text">
              {sellPrice}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MaterialGalleryCard({
  item,
  comment,
  detailProduct,
  detailError,
  isActive,
  imageSizes,
  originalIndex,
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
  onIntent,
  onIntentEnd,
  onOpen,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: {
  item: MaterialGalleryItem;
  comment?: SeedingFeaturedComment | null;
  detailProduct?: SeedingDetailProduct;
  detailError?: Error;
  isActive: boolean;
  imageSizes: string;
  originalIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
  onIntent: (itemId: number) => void;
  onIntentEnd: () => void;
  onOpen: (itemId: number, originalIndex: number) => void;
} & Pick<MaterialGallery2026Props, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'>) {
  const itemId = Number(item.id);
  const accessibleName = item.kol_name?.trim() || item.shop_sku?.trim() || '';
  const commentText = comment?.content?.trim() || '';
  const imageAlt =
    Array.from(commentText).slice(0, REVIEW_IMAGE_ALT_MAX_LENGTH).join('') || accessibleName;
  const trackingParams = useMemo(
    () =>
      buildMaterialTrackingParams({
        item,
        originalIndex,
        componentId,
        moduleId,
        moduleTitle,
        moduleIndex,
        product: detailProduct,
      }),
    [componentId, detailProduct, item, moduleId, moduleIndex, moduleTitle, originalIndex],
  );
  const validExposureParams = buildValidExposureParams(trackingParams);
  const { cardRef, handleClick } = useMaterialTracking({
    trackingParams,
    validExposureParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  });

  const handleOpen = useCallback(() => {
    preloadSeedingItemDialog();
    handleClick();
    onOpen(itemId, originalIndex);
  }, [handleClick, itemId, onOpen, originalIndex]);

  const handleIntent = useCallback(() => {
    preloadSeedingItemDialog();
    onIntent(itemId);
  }, [itemId, onIntent]);

  return (
    <div ref={cardRef} className="h-full min-w-0">
      <article className="flex min-w-0 flex-col gap-[8px]">
        <button
          type="button"
          aria-label={accessibleName || undefined}
          data-material-id={itemId}
          className="group relative aspect-[474/592] w-full cursor-pointer overflow-hidden rounded-[12px] bg-auxiliary-light-gray text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-purple"
          onPointerEnter={handleIntent}
          onPointerLeave={onIntentEnd}
          onFocus={handleIntent}
          onBlur={onIntentEnd}
          onClick={handleOpen}
        >
          <Image
            src={item.jump_image || ''}
            alt={imageAlt}
            width={474}
            height={592}
            loading="lazy"
            sizes={imageSizes}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.01] group-focus-visible:scale-[1.01]"
          />

          {isActive && !detailError ? (
            <div className="absolute inset-0 flex items-end bg-black/20 p-[16px]">
              <MaterialProductPreview product={detailProduct} />
            </div>
          ) : null}
        </button>

        <div className="min-h-[34px] px-[6px] py-[8px]">
          {commentText ? (
            <p className="line-clamp-2 whitespace-pre-line break-words font-br text-[14px] leading-[17px] text-[#151618]">
              {commentText}
            </p>
          ) : null}
        </div>
      </article>
    </div>
  );
}

function MaterialGalleryContent({
  indexData,
  moduleIndex,
  validItems,
  pageId,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: MaterialGallery2026Props & {
  validItems: ReturnType<typeof getValidMaterialItems>;
  pageId: number;
}) {
  const [hoveredItemId, setHoveredItemId] = useState<number | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<SelectedMaterial | null>(null);
  const { trackEvent } = useReporting();
  const tSeeding = useTranslations('activity.campaigns.seeding');
  const navigationId = useId().replace(/:/g, '');
  const navigationPrefix = `material-gallery-${navigationId}`;
  const isPreview =
    typeof window !== 'undefined' && window.location.pathname.includes(HOME_PREVIEW_PATH);
  const [summaryRef, shouldLoadSummary] = useInView({
    rootMargin: '400px 0px',
    triggerOnce: true,
  });
  const activeItemId = selectedMaterial?.itemId ?? hoveredItemId;
  const { data: summaryData } = useMaterialGallerySummary(pageId, isPreview, shouldLoadSummary);
  const {
    data: detail,
    error: detailError,
    isLoading: detailLoading,
  } = useMaterialGalleryDetail(pageId, activeItemId, isPreview);
  const summaryMap = useMemo(
    () => buildSeedingSummaryMap(summaryData?.items),
    [summaryData?.items],
  );

  const componentId = indexData.component_id || COMPONENT_ID;
  const moduleId = String(indexData.blocks_advances_id || '');
  const configuredTitle = normalizeConfiguredText(indexData.layout_title);
  const configuredSubtitle = normalizeConfiguredText(indexData.layout_subtitle);
  const hasConfiguredHeading = Boolean(configuredTitle || configuredSubtitle);
  const shouldShowConfiguredHeading =
    Number(indexData.layout_title_status) === 1 && hasConfiguredHeading;
  const moduleTitle = configuredTitle;
  const headingTitle = shouldShowConfiguredHeading ? configuredTitle : '';
  const headingSubtitle = shouldShowConfiguredHeading ? configuredSubtitle : '';
  const headingTitleColor = indexData.layout_title_color?.trim() || DEFAULT_HEADING_COLOR;
  const headingSubtitleColor =
    indexData.layout_subtitle_color?.trim() ||
    indexData.layout_subtitl_color?.trim() ||
    DEFAULT_SUBTITLE_COLOR;
  const shouldShowHeading = Boolean(headingTitle || headingSubtitle);
  const buttonText = normalizeConfiguredText(indexData.btn_text);
  const pageUrl = indexData.page_url?.trim() || '';
  const shouldShowPageLink = Boolean(buttonText && pageUrl);
  const visibleColumnCount = Math.min(validItems.length, 4);
  const canNavigate = validItems.length > visibleColumnCount;
  const imageSizes = `calc(${Number((100 / visibleColumnCount).toFixed(3))}vw - ${
    (96 + (visibleColumnCount - 1) * 12) / visibleColumnCount
  }px)`;

  const selectedEntry = selectedMaterial
    ? validItems.find(({ item }) => Number(item.id) === selectedMaterial.itemId)
    : undefined;
  const isDialogOpen = Boolean(selectedEntry && selectedMaterial);

  useEffect(() => {
    if (!isDialogOpen) return;

    try {
      trackEvent(
        {
          eventName: 'PopUp_Exp',
          properties: {
            popup_type: MATERIAL_DETAIL_POPUP_TYPE,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('2026 PC material gallery popup exposure tracking error:', error);
    }
  }, [isDialogOpen, trackEvent]);

  const handlePageTrack = useCallback(() => {
    if (!onTrack) return;

    try {
      onTrack(
        buildValidExposureParams(
          buildGalleryPageTrackingParams({
            componentId,
            moduleId,
            moduleTitle,
            moduleIndex,
            pageUrl,
          }),
        ),
      );
    } catch (error) {
      logger.error('2026 PC material gallery page click tracking error:', error);
    }
  }, [componentId, moduleId, moduleIndex, moduleTitle, onTrack, pageUrl]);

  const handleOpen = useCallback((itemId: number, originalIndex: number) => {
    setSelectedMaterial({ itemId, originalIndex });
  }, []);

  const handleClose = useCallback(() => {
    setSelectedMaterial(null);
    setHoveredItemId(null);
  }, []);

  const handleIntentEnd = useCallback(() => {
    setHoveredItemId(null);
  }, []);

  const handleProductClick = useCallback(() => {
    try {
      trackEvent(
        {
          eventName: 'PopUp_Click',
          properties: {
            popup_type: MATERIAL_DETAIL_POPUP_TYPE,
            btn_name: tSeeding('buyNow'),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('2026 PC material gallery popup click tracking error:', error);
    }

    if (!onTrack || !selectedEntry || !detail?.product || !selectedMaterial) return;

    try {
      onTrack(
        buildValidExposureParams(
          buildMaterialTrackingParams({
            item: selectedEntry.item,
            originalIndex: selectedMaterial.originalIndex,
            componentId,
            moduleId,
            moduleTitle,
            moduleIndex,
            product: detail.product,
          }),
        ),
      );
    } catch (error) {
      logger.error('2026 PC material gallery product click tracking error:', error);
    }
  }, [
    componentId,
    detail?.product,
    moduleId,
    moduleIndex,
    moduleTitle,
    onTrack,
    selectedEntry,
    selectedMaterial,
    tSeeding,
    trackEvent,
  ]);

  return (
    <section
      ref={summaryRef}
      data-component-id={componentId}
      className="swiper-nav-container relative w-full bg-primary-white"
    >
      {shouldShowHeading || shouldShowPageLink ? (
        <header className="relative flex min-h-[80px] w-full items-center justify-center px-[180px] py-[28px] text-center">
          {shouldShowHeading ? (
            <div className="flex min-w-0 flex-col items-center gap-[8px]">
              {headingTitle ? (
                <h3
                  className="whitespace-pre-line font-bb text-[32px] leading-[39px]"
                  style={{ color: headingTitleColor }}
                >
                  {headingTitle}
                </h3>
              ) : null}
              {headingSubtitle ? (
                <h4
                  className="whitespace-pre-line font-br text-[20px] leading-[24px]"
                  style={{ color: headingSubtitleColor }}
                >
                  {headingSubtitle}
                </h4>
              ) : null}
            </div>
          ) : null}

          {shouldShowPageLink ? (
            <MagicJump
              prefetch
              item={{ jump_type: 4, Jump_target: pageUrl }}
              aria-label={buttonText}
              onBeforeJump={handlePageTrack}
              className="absolute right-[48px] top-1/2 flex -translate-y-1/2 items-center gap-[8px] font-br text-[16px] leading-[19px] text-primary-text"
            >
              <span>{buttonText}</span>
              <RightArrowOutlined className="text-[12px]" aria-hidden />
            </MagicJump>
          ) : null}
        </header>
      ) : null}

      <div className="w-full px-[48px] pb-[24px]">
        <Swiper
          modules={[Navigation]}
          slidesPerView={visibleColumnCount}
          slidesPerGroup={1}
          loop={canNavigate}
          spaceBetween={12}
          watchOverflow
          navigation={
            canNavigate
              ? {
                  prevEl: `.${navigationPrefix}-prev`,
                  nextEl: `.${navigationPrefix}-next`,
                }
              : false
          }
          wrapperTag="ul"
          className="!overflow-hidden"
        >
          {validItems.map(({ item, originalIndex }) => {
            const itemId = Number(item.id);
            const summary = summaryMap.get(itemId);
            const isActive = activeItemId === itemId;

            return (
              // Swiper 初始化前 slide 默认为 100% 宽且没有 spaceBetween，首帧先对齐最终布局。
              <SwiperSlide
                key={itemId}
                tag="li"
                className="!h-auto min-w-0 shrink-0"
                style={{ width: imageSizes, marginRight: 12 }}
              >
                <MaterialGalleryCard
                  item={item}
                  comment={summary?.comment}
                  detailProduct={isActive ? detail?.product : undefined}
                  detailError={isActive ? detailError : undefined}
                  isActive={isActive}
                  imageSizes={imageSizes}
                  originalIndex={originalIndex}
                  componentId={componentId}
                  moduleId={moduleId}
                  moduleTitle={moduleTitle}
                  moduleIndex={moduleIndex}
                  onIntent={setHoveredItemId}
                  onIntentEnd={handleIntentEnd}
                  onOpen={handleOpen}
                  onTrack={onTrack}
                  onExpTrack={onExpTrack}
                  onValidExpTrack={onValidExpTrack}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {canNavigate ? (
        <SwiperNavigationButtons
          navigationPrefix={navigationPrefix}
          prevButtonPosition="left-[clamp(24px,calc(-40px_+_6.25vw),40px)] 3xl:left-[56px]"
          nextButtonPosition="right-[clamp(24px,calc(-40px_+_6.25vw),40px)] 3xl:right-[56px]"
          prevButtonClassName="!top-[calc(50%+22px)] !z-[400] !size-[32px] !bg-black/30 hover:!scale-100 hover:!bg-black/30 focus-visible:pointer-events-auto focus-visible:opacity-100"
          nextButtonClassName="!top-[calc(50%+22px)] !z-[400] !size-[32px] !bg-black/30 hover:!scale-100 hover:!bg-black/30 focus-visible:pointer-events-auto focus-visible:opacity-100"
        />
      ) : null}

      {selectedEntry && selectedMaterial ? (
        <SeedingItemDialog
          open
          loading={detailLoading}
          detail={activeItemId === selectedMaterial.itemId ? (detail ?? null) : null}
          onClose={handleClose}
          isWap={false}
          index={selectedMaterial.originalIndex}
          trackCampaignPopupCollectClick={ignoreMaterialGalleryCollectTracking}
          trackCampaignPopupClick={handleProductClick}
        />
      ) : null}
    </section>
  );
}

export default function MaterialGallery2026(props: MaterialGallery2026Props) {
  const { indexData } = props;
  const validItems = useMemo(
    () => getValidMaterialItems(indexData.material_list),
    [indexData.material_list],
  );
  const pageId = Number(indexData.page_id);

  if (!Number.isFinite(pageId) || pageId <= 0 || validItems.length === 0) return null;

  return <MaterialGalleryContent key={pageId} {...props} pageId={pageId} validItems={validItems} />;
}
